<script setup>
import { formatNumber, formatRupiah } from '@/composables/useFormat'
import { useAuthStore } from '@/stores/auth'
import { useStationStore } from '@/stores/station'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Dashboard',
  },
})

const ability = useAbility()
const authStore = useAuthStore()
const stationStore = useStationStore()
const router = useRouter()

// KDS quick access — pakai endpoint /v1/stations yang sudah ada (Modul 16),
// TIDAK perlu ubah backend/DashboardController sama sekali.
const isLoadingStations = ref(false)
const canViewStations = computed(() => ability.can('read', 'Station'))

// ── State ────────────────────────────────────────────────────────────────────
const loading = ref(false)
const selectedDate = ref(new Date().toISOString().slice(0, 10))

const sales = ref({
  total_orders: 0,
  revenue: 0,
  hpp: 0,
  gross_profit: 0,
  total_discount: 0,
  total_tax: 0,
  total_refund: 0,
})
const expenses = ref({ total: 0 })
const topProducts = ref([])
const lowStock = ref([])
const shift = ref(null)
const recentOrders = ref([])
const needsStaffOnboarding = ref(false)

// Check user role
const isOwner = computed(() => ability.can('manage', 'all'))
const isCashier = computed(() => ability.can('create', 'Sales'))

// ── Fetch ──────────────────────────────────────────────────────────────────────
const fetchDashboard = async () => {
  loading.value = true
  try {
    const response = await useApi('/v1/reports/dashboard', {
      query: { date: selectedDate.value },
    }).get()
    
    const data = response?.data ?? response

    sales.value = data?.sales ?? sales.value
    expenses.value = data?.expenses ?? expenses.value
    topProducts.value = data?.top_products ?? []
    lowStock.value = data?.low_stock ?? []
    shift.value = data?.shift ?? null
    recentOrders.value = data?.recent_orders ?? []
    needsStaffOnboarding.value = data?.needs_staff_onboarding ?? false
  } catch (err) {
    console.error('❌ Dashboard error:', err)
  } finally {
    loading.value = false
  }
}

const fetchStations = async () => {
  if (!canViewStations.value)
    return

  isLoadingStations.value = true
  try {
    await stationStore.fetchStations()
  }
  catch (err) {
    console.error('❌ Fetch stations error:', err)
  }
  finally {
    isLoadingStations.value = false
  }
}

onMounted(() => {
  fetchDashboard()
  fetchStations()
})
watch(selectedDate, fetchDashboard)

const openStationDisplay = station => {
  // Validasi station
  if (!station) {
    console.error('❌ Station object tidak ada')
    return
  }
  
  if (!station.id) {
    console.error('❌ Station ID tidak valid:', station)
    return
  }
  
  // Pastikan ID adalah number
  const stationId = Number(station.id)
  
  // Coba navigate dengan route name
  try {
    router.push({ 
      name: 'kds-station-id',
      params: { stationId } 
    })
  } catch (err) {
    console.error('❌ Route navigation error:', err)
    // Fallback: pakai path langsung
    router.push(`/kds/${stationId}`)
  }
}

// ── Computed ──────────────────────────────────────────────────────────────────
// Modul AV (Addendum 11 §5): refund selalu tunai keluar, mengurangi laba
// bersih terlepas dari metode bayar order aslinya -- gross_profit sendiri
// SENGAJA tidak diubah (margin kotor tetap murni revenue-HPP, refund itu
// peristiwa pasca-jual, bukan bagian dari margin kotor transaksi).
const netProfit = computed(() => sales.value.gross_profit - expenses.value.total - sales.value.total_refund)

const grossMargin = computed(() => {
  if (!sales.value.revenue) return 0
  return ((sales.value.gross_profit / sales.value.revenue) * 100).toFixed(1)
})

// Stats cards untuk Owner
const ownerStats = computed(() => [
  {
    label: 'Omzet Hari Ini',
    value: formatRupiah(sales.value.revenue),
    icon: 'ri-money-dollar-circle-line',
    color: 'primary',
    sub: `${formatNumber(sales.value.total_orders)} transaksi`,
  },
  {
    label: 'Laba Kotor',
    value: formatRupiah(sales.value.gross_profit),
    icon: 'ri-line-chart-line',
    color: 'success',
    sub: `Margin ${grossMargin.value}%`,
  },
  {
    label: 'Pengeluaran',
    value: formatRupiah(expenses.value.total),
    icon: 'ri-wallet-line',
    color: 'error',
    sub: 'Biaya operasional',
  },
  {
    label: 'Laba Bersih',
    value: formatRupiah(netProfit.value),
    icon: 'ri-trophy-line',
    color: netProfit.value >= 0 ? 'success' : 'error',
    sub: netProfit.value >= 0 ? 'Profit' : 'Rugi',
  },
])

// Stats cards untuk Kasir
const cashierStats = computed(() => [
  {
    label: 'Total Transaksi',
    value: formatNumber(sales.value.total_orders),
    icon: 'ri-shopping-cart-line',
    color: 'primary',
    sub: 'Hari ini',
  },
  {
    label: 'Total Penjualan',
    value: formatRupiah(sales.value.revenue),
    icon: 'ri-money-dollar-circle-line',
    color: 'success',
    sub: 'Termasuk pajak',
  },
  {
    label: 'Diskon Diberikan',
    value: formatRupiah(sales.value.total_discount),
    icon: 'ri-discount-line',
    color: 'warning',
    sub: 'Total diskon',
  },
  {
    label: 'Stok Kritis',
    value: formatNumber(lowStock.value.length),
    icon: 'ri-archive-line',
    color: lowStock.value.length > 0 ? 'error' : 'success',
    sub: lowStock.value.length > 0 ? 'Perlu restok' : 'Stok aman',
  },
])

// Pilih stats berdasarkan role
const statCards = computed(() => isOwner.value ? ownerStats.value : cashierStats.value)

const avatarColors = ['primary', 'success', 'info', 'warning', 'error']

// ── Actions ───────────────────────────────────────────────────────────────────
const goToPOS = () => {
  router.push({ name: 'pos' })
}

const goToOrders = () => {
  router.push({ name: 'sales-orders' })
}

const goToInventory = () => {
  router.push({ name: 'inventory-ingredients' })
}
</script>

<template>
  <div>
    <!-- Page Header -->
    <VRow class="mb-4 align-center">
      <VCol cols="12" md="6">
        <h4 class="text-h4 font-weight-medium mb-1">
          Dashboard
          <VChip v-if="isCashier" color="primary" size="small" class="ml-2">
            Mode Kasir
          </VChip>
          <VChip v-else-if="isOwner" color="success" size="small" class="ml-2">
            Mode Owner
          </VChip>
        </h4>
        <p class="text-body-2 text-medium-emphasis">
          {{ isOwner ? 'Ringkasan performa bisnis Anda' : 'Ringkasan aktivitas kasir hari ini' }}
        </p>
      </VCol>
      <VCol cols="12" md="3" offset-md="3">
        <VTextField
          v-model="selectedDate"
          type="date"
          label="Tanggal"
          density="compact"
          hide-details
          :loading="loading"
        />
      </VCol>
    </VRow>

    <!-- Onboarding Nudge (Modul E — belum ada akun staf selain owner) -->
    <VAlert
      v-if="isOwner && needsStaffOnboarding"
      type="warning"
      variant="tonal"
      class="mb-4"
      icon="ri-user-add-line"
      closable
    >
      <div class="d-flex align-center justify-space-between flex-wrap gap-2">
        <div>
          <strong>Anda belum membuat akun Cashier</strong> — akun Owner sebaiknya
          tidak dipakai untuk operasional kasir sehari-hari.
        </div>
        <VBtn
          :to="{ name: 'apps-user-list' }"
          color="warning"
          variant="elevated"
          size="small"
          prepend-icon="ri-user-add-line"
        >
          Buat Akun Staf
        </VBtn>
      </div>
    </VAlert>

    <!-- Shift Banner (untuk Kasir) -->
    <VAlert
      v-if="shift && isCashier"
      type="info"
      variant="tonal"
      class="mb-4"
      icon="ri-time-line"
      closable
    >
      <div class="d-flex align-center justify-space-between flex-wrap gap-2">
        <div>
          <strong>Shift Aktif</strong> — Dibuka sejak {{ shift.opened_at ? new Date(shift.opened_at).toLocaleString('id-ID') : '-' }}
        </div>
        <div>
          <VChip color="success" variant="tonal">
            Kas Awal: {{ formatRupiah(shift.opening_cash) }}
          </VChip>
        </div>
      </div>
    </VAlert>

    <!-- Quick Actions (untuk Kasir) -->
    <VCard v-if="isCashier" class="mb-4">
      <VCardText>
        <VRow>
          <VCol cols="12" md="4">
            <VBtn
              color="primary"
              block
              size="large"
              prepend-icon="ri-shopping-cart-line"
              @click="goToPOS"
            >
              Buka POS Kasir
            </VBtn>
          </VCol>
          <VCol cols="12" md="4">
            <VBtn
              color="success"
              variant="outlined"
              block
              size="large"
              prepend-icon="ri-receipt-line"
              @click="goToOrders"
            >
              Lihat Order Hari Ini
            </VBtn>
          </VCol>
          <VCol cols="12" md="4">
            <VBtn
              color="info"
              variant="outlined"
              block
              size="large"
              prepend-icon="ri-archive-line"
              @click="goToInventory"
            >
              Cek Stok Bahan
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Kitchen Display (KDS) — daftar stasiun langsung dari database -->
    <VCard v-if="canViewStations" class="mb-4" :loading="isLoadingStations">
      <VCardItem>
        <VCardTitle class="text-body-1 font-weight-semibold">
          Kitchen Display (KDS)
        </VCardTitle>
        <VCardSubtitle>
          Buka layar monitor dapur/barista per stasiun
        </VCardSubtitle>
      </VCardItem>
      <VCardText>
        <VAlert v-if="!isLoadingStations && !stationStore.stations.length" type="info" variant="tonal">
          Belum ada stasiun. Tambahkan dulu di menu Pengaturan &gt; Stasiun.
        </VAlert>
        <VRow v-else>
          <VCol
            v-for="station in stationStore.stations"
            :key="station.id"
            cols="12"
            sm="6"
            md="3"
          >
          <VBtn
            block
            variant="tonal"
            color="primary"
            prepend-icon="ri-tv-2-line"
            @click="() => {
              openStationDisplay(station)
            }"
            :disabled="!station?.id"
          >
            {{ station.name }}
            <span v-if="!station?.id" class="text-caption ml-1">(No ID)</span>
          </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Stat Cards -->
    <VRow class="mb-4">
      <VCol
        v-for="card in statCards"
        :key="card.label"
        cols="12"
        sm="6"
        lg="3"
      >
        <VCard :loading="loading">
          <VCardText>
            <div class="d-flex align-center mb-3">
              <VAvatar :color="card.color" variant="tonal" size="44">
                <VIcon :icon="card.icon" size="22" />
              </VAvatar>
            </div>
            <div class="text-h5 font-weight-bold mb-1">{{ card.value }}</div>
            <div class="text-body-2 text-medium-emphasis">{{ card.label }}</div>
            <div v-if="card.sub" class="text-caption mt-1" :class="`text-${card.color}`">
              {{ card.sub }}
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Detail Row -->
    <VRow>
      <!-- Breakdown Keuangan (Owner only) -->
      <VCol v-if="isOwner" cols="12" md="4">
        <VCard :loading="loading" height="100%">
          <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2">
            Ringkasan Keuangan
          </VCardTitle>
          <VDivider />
          <VList density="compact">
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-arrow-up-circle-line" color="success" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">Omzet</VListItemTitle>
              <template #append>
                <span class="text-body-2 font-weight-medium text-success">
                  {{ formatRupiah(sales.revenue) }}
                </span>
              </template>
            </VListItem>
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-subtract-line" color="warning" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">Diskon</VListItemTitle>
              <template #append>
                <span class="text-body-2 text-warning">
                  ({{ formatRupiah(sales.total_discount) }})
                </span>
              </template>
            </VListItem>
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-subtract-line" color="warning" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">HPP</VListItemTitle>
              <template #append>
                <span class="text-body-2 text-warning">
                  ({{ formatRupiah(sales.hpp) }})
                </span>
              </template>
            </VListItem>
            <VDivider class="my-1" />
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-line-chart-line" color="info" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2 font-weight-semibold">Laba Kotor</VListItemTitle>
              <template #append>
                <span class="text-body-2 font-weight-bold text-info">
                  {{ formatRupiah(sales.gross_profit) }}
                </span>
              </template>
            </VListItem>
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-subtract-line" color="error" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">Pengeluaran</VListItemTitle>
              <template #append>
                <span class="text-body-2 text-error">
                  ({{ formatRupiah(expenses.total) }})
                </span>
              </template>
            </VListItem>
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-subtract-line" color="error" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">Refund</VListItemTitle>
              <template #append>
                <span class="text-body-2 text-error">
                  ({{ formatRupiah(sales.total_refund) }})
                </span>
              </template>
            </VListItem>
            <VDivider class="my-1" />
            <VListItem>
              <template #prepend>
                <VIcon
                  :icon="netProfit >= 0 ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'"
                  :color="netProfit >= 0 ? 'success' : 'error'"
                  size="18"
                  class="mr-2"
                />
              </template>
              <VListItemTitle class="text-body-2 font-weight-bold">Laba Bersih</VListItemTitle>
              <template #append>
                <span
                  class="text-body-2 font-weight-bold"
                  :class="netProfit >= 0 ? 'text-success' : 'text-error'"
                >
                  {{ formatRupiah(netProfit) }}
                </span>
              </template>
            </VListItem>
          </VList>
        </VCard>
      </VCol>

      <!-- Top Produk (Both roles) -->
      <VCol cols="12" :md="isOwner ? 4 : 6">
        <VCard :loading="loading" height="100%">
          <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2">
            Top 5 Produk Terlaris
          </VCardTitle>
          <VDivider />
          <!-- Empty state -->
          <VCardText v-if="!loading && topProducts.length === 0" class="text-center py-8">
            <VIcon icon="ri-cup-line" size="48" color="secondary" class="mb-2" />
            <p class="text-body-2 text-medium-emphasis">Belum ada transaksi hari ini</p>
          </VCardText>
          <VList v-else density="compact">
            <VListItem
              v-for="(item, i) in topProducts"
              :key="i"
            >
              <template #prepend>
                <VAvatar
                  :color="avatarColors[i] || 'secondary'"
                  variant="tonal"
                  size="32"
                  class="text-caption font-weight-bold mr-3"
                >
                  {{ i + 1 }}
                </VAvatar>
              </template>
              <VListItemTitle class="text-body-2">
                {{ item.product_name }}
                <span v-if="item.variant_name" class="text-medium-emphasis"> · {{ item.variant_name }}</span>
              </VListItemTitle>
              <VListItemSubtitle>{{ formatNumber(item.total_qty) }} terjual</VListItemSubtitle>
              <template #append>
                <span class="text-body-2 text-success font-weight-medium">
                  {{ formatRupiah(item.total_revenue) }}
                </span>
              </template>
            </VListItem>
          </VList>
        </VCard>
      </VCol>

      <!-- Low Stock Alert (Both roles) -->
      <VCol cols="12" :md="isOwner ? 4 : 6">
        <VCard :loading="loading" height="100%">
          <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2 d-flex align-center">
            Stok Kritis
            <VChip
              v-if="lowStock.length > 0"
              color="error"
              size="small"
              class="ml-2"
            >
              {{ lowStock.length }}
            </VChip>
          </VCardTitle>
          <VDivider />
          <!-- Empty / all ok -->
          <VCardText v-if="!loading && lowStock.length === 0" class="text-center py-8">
            <VIcon icon="ri-checkbox-circle-line" size="48" color="success" class="mb-2" />
            <p class="text-body-2 text-medium-emphasis">Semua stok aman</p>
          </VCardText>
          <VList v-else density="compact">
            <VListItem
              v-for="item in lowStock"
              :key="item.id"
            >
              <template #prepend>
                <VIcon icon="ri-error-warning-line" color="error" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">{{ item.name }}</VListItemTitle>
              <VListItemSubtitle class="text-caption text-error">
                Stok: {{ item.current_stock }} {{ item.unit }} (min {{ item.minimum_stock }})
              </VListItemSubtitle>
            </VListItem>
          </VList>
          <VCardActions v-if="lowStock.length > 0 && ability.can('create', 'Purchase')">
            <VBtn
              :to="{ name: 'purchase-orders-create' }"
              color="error"
              variant="tonal"
              size="small"
              prepend-icon="ri-shopping-bag-line"
              block
            >
              Buat Purchase Order
            </VBtn>
          </VCardActions>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
